import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './Performance.module.css';
import { strategyEngineService } from '../../../services/strategyEngineService';
import { formatISTShort } from '../../../utils/datetime';

// Read-only live view of the engine's evaluation trace + SMA series.

const TRACE_INTERVAL = 3000;
const SERIES_INTERVAL = 15000;
const MAX_TRACE_FAILURES = 3;

const fmt = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const OP_LABEL = {
  'Is Greater Than': '>',
  'Is Less Than': '<',
  'Is Greater Than Or Equal To': '≥',
  'Is Less Than Or Equal To': '≤',
  Equals: '=',
  'Is Equal To': '=',
  'Crosses Above': 'crosses above',
  'Crosses Below': 'crosses below',
  'Is After': 'after',
  'Is Before': 'before',
  'Is At Or After': 'at/after',
  'Is At Or Before': 'at/before',
};

// SMA-10 vs SMA-20 (+ price) chart with signal markers.
function SmaChart({ points, signals }) {
  const svgRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  if (!points || points.length < 2) {
    return (
      <div className={styles.chartEmpty}>
        No SMA data yet for this symbol — the engine needs ~20 candles to
        compute SMA-10/20. The chart appears once enough candles exist.
      </div>
    );
  }

  const W = 1200;
  const H = 360;
  const padL = 62;
  const padR = 70;
  const padT = 18;
  const padB = 30;

  const vals = [];
  points.forEach((p) => {
    [p.sma10, p.sma20, p.close].forEach((v) => {
      if (Number.isFinite(v)) vals.push(v);
    });
  });
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const yMin = min - span * 0.08;
  const yMax = max + span * 0.08;
  const n = points.length;

  const x = (i) => padL + (i / (n - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - yMin) / (yMax - yMin)) * (H - padT - padB);

  const path = (key) =>
    points
      .map((p, i) =>
        Number.isFinite(p[key]) ? `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}` : ''
      )
      .filter(Boolean)
      .join(' ');

  const last = (key) => {
    for (let i = n - 1; i >= 0; i--) if (Number.isFinite(points[i][key])) return points[i][key];
    return null;
  };

  const times = points.map((p) => new Date(p.time).getTime());
  const markers = (signals || [])
    .map((s) => {
      const t = new Date(s.signal_time || s.created_at).getTime();
      if (!Number.isFinite(t) || t < times[0] || t > times[n - 1]) return null;
      let idx = 0;
      let best = Infinity;
      for (let i = 0; i < n; i++) {
        const d = Math.abs(times[i] - t);
        if (d < best) {
          best = d;
          idx = i;
        }
      }
      const anchor = Number.isFinite(points[idx].sma10) ? points[idx].sma10 : points[idx].close;
      if (!Number.isFinite(anchor)) return null;
      return { cx: x(idx), cy: y(anchor), buy: s.action_type === 'BUY' };
    })
    .filter(Boolean);

  const yTicks = [yMax, (3 * yMax + yMin) / 4, (yMax + yMin) / 2, (yMax + 3 * yMin) / 4, yMin];
  const lastSma10 = last('sma10');
  const lastSma20 = last('sma20');

  // Map the cursor to the nearest candle index using the SVG's own coordinate
  // transform (works regardless of how the responsive SVG is scaled).
  const handleMove = (e) => {
    const svg = svgRef.current;
    if (!svg || !svg.getScreenCTM) return;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const loc = pt.matrixTransform(ctm.inverse());
    const ratio = (loc.x - padL) / (W - padL - padR);
    const i = Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1))));
    setHoverIdx(i);
  };
  const handleLeave = () => setHoverIdx(null);

  const hp = hoverIdx != null && hoverIdx >= 0 && hoverIdx < n ? points[hoverIdx] : null;
  const hx = hp ? x(hoverIdx) : 0;
  const tipW = 132;
  const tipH = 76;
  const tipX = hp ? (hx + 14 + tipW > W - padR ? hx - 14 - tipW : hx + 14) : 0;
  const tipY = padT + 6;
  const hTime = hp ? formatISTShort(hp.time) : '';

  return (
    <svg ref={svgRef} className={styles.chart} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} className={styles.gridLine} />
          <text x={padL - 10} y={y(v) + 3} className={styles.axis} textAnchor="end">
            {fmt(v)}
          </text>
        </g>
      ))}
      {Number.isFinite(points[0].close) && <path d={path('close')} className={styles.lineClose} fill="none" />}
      <path d={path('sma20')} className={styles.lineSma20} fill="none" />
      <path d={path('sma10')} className={styles.lineSma10} fill="none" />
      {Number.isFinite(lastSma10) && (
        <text x={W - padR + 6} y={y(lastSma10) + 3} className={styles.endLabelSma10}>{fmt(lastSma10)}</text>
      )}
      {Number.isFinite(lastSma20) && (
        <text x={W - padR + 6} y={y(lastSma20) + 3} className={styles.endLabelSma20}>{fmt(lastSma20)}</text>
      )}
      {markers.map((m, i) => (
        <circle key={i} cx={m.cx} cy={m.cy} r={5} className={m.buy ? styles.markerBuy : styles.markerSell} />
      ))}

      {hp && (
        <g pointerEvents="none">
          <line x1={hx} y1={padT} x2={hx} y2={H - padB} className={styles.crosshair} />
          {Number.isFinite(hp.close) && <circle cx={hx} cy={y(hp.close)} r={3.5} className={styles.dotClose} />}
          {Number.isFinite(hp.sma20) && <circle cx={hx} cy={y(hp.sma20)} r={3.5} className={styles.dotSma20} />}
          {Number.isFinite(hp.sma10) && <circle cx={hx} cy={y(hp.sma10)} r={3.5} className={styles.dotSma10} />}
          <rect x={tipX} y={tipY} width={tipW} height={tipH} rx={6} className={styles.tipBox} />
          <text x={tipX + 10} y={tipY + 18} className={styles.tipTime}>{hTime}</text>
          <text x={tipX + 10} y={tipY + 36} className={styles.tipRow}>
            <tspan className={styles.tipLabelSma10}>SMA10 </tspan>{fmt(hp.sma10)}
          </text>
          <text x={tipX + 10} y={tipY + 51} className={styles.tipRow}>
            <tspan className={styles.tipLabelSma20}>SMA20 </tspan>{fmt(hp.sma20)}
          </text>
          <text x={tipX + 10} y={tipY + 66} className={styles.tipRow}>
            <tspan className={styles.tipLabelClose}>Close </tspan>{fmt(hp.close)}
          </text>
        </g>
      )}

      <rect
        x={padL}
        y={padT}
        width={W - padL - padR}
        height={H - padT - padB}
        fill="transparent"
        style={{ cursor: 'crosshair' }}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
      />
    </svg>
  );
}

function ChartLegend() {
  return (
    <div className={styles.legend}>
      <span className={styles.legendItem}><span className={`${styles.swatchLine} ${styles.sw10}`} /> SMA 10</span>
      <span className={styles.legendItem}><span className={`${styles.swatchLine} ${styles.sw20}`} /> SMA 20</span>
      <span className={styles.legendItem}><span className={`${styles.swatchLine} ${styles.swPrice}`} /> Price (close)</span>
      <span className={styles.legendItem}><span className={`${styles.swatchDot} ${styles.swBuy}`} /> Buy signal</span>
      <span className={styles.legendItem}><span className={`${styles.swatchDot} ${styles.swSell}`} /> Sell signal</span>
    </div>
  );
}

function ConditionRow({ c }) {
  const ok = c.result === true;
  return (
    <div className={`${styles.condRow} ${ok ? styles.condPass : styles.condFail}`}>
      <span className={styles.condDot}>{ok ? '✓' : '✗'}</span>
      <span className={styles.condText}>
        {c.kind === 'time' ? (
          <>
            <b>{c.variable}</b> {OP_LABEL[c.operator] || c.operator} <b>{c.value}</b>
          </>
        ) : (
          <>
            <b>{c.variable}</b>
            {c.variableTimeframe ? <span className={styles.tf}>@{c.variableTimeframe}</span> : null} (
            {fmt(c.actualValue)}) {OP_LABEL[c.operator] || c.operator} <b>{c.value}</b> ({fmt(c.compareValue)})
          </>
        )}
      </span>
      {c.logicalOperator ? <span className={styles.logic}>{c.logicalOperator}</span> : null}
    </div>
  );
}

// Rail showing where the live price sits among stop / entry / peak / target.
function RiskRail({ action, entry, peak, ltp, stop, target }) {
  const pts = [];
  if (Number.isFinite(stop)) pts.push({ key: 'stop', label: 'Stop', value: stop, color: 'var(--error-red)' });
  if (Number.isFinite(entry)) pts.push({ key: 'entry', label: 'Entry', value: entry, color: 'var(--text-secondary)' });
  if (Number.isFinite(peak) && peak !== entry) pts.push({ key: 'peak', label: 'Peak', value: peak, color: 'var(--accent-blue)' });
  if (Number.isFinite(target)) pts.push({ key: 'target', label: 'Target', value: target, color: 'var(--success-green)' });

  const ltpValid = Number.isFinite(ltp);
  const all = pts.map((p) => p.value).concat(ltpValid ? [ltp] : []);
  if (all.length < 2) return null;

  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const pos = (v) => ((v - min) / range) * 100;
  const clampPct = (p) => Math.max(2, Math.min(98, p));

  const isLong = action === 'BUY';
  const dangerA = Number.isFinite(stop) && Number.isFinite(entry) ? Math.min(pos(stop), pos(entry)) : null;
  const dangerW = Number.isFinite(stop) && Number.isFinite(entry) ? Math.abs(pos(entry) - pos(stop)) : 0;
  const profitA = Number.isFinite(entry) && Number.isFinite(target) ? Math.min(pos(entry), pos(target)) : null;
  const profitW = Number.isFinite(entry) && Number.isFinite(target) ? Math.abs(pos(target) - pos(entry)) : 0;

  return (
    <div className={styles.rail}>
      <div className={styles.railTrack}>
        {dangerA !== null && (
          <div className={styles.railDanger} style={{ left: `${dangerA}%`, width: `${dangerW}%` }} />
        )}
        {profitA !== null && (
          <div className={styles.railProfit} style={{ left: `${profitA}%`, width: `${profitW}%` }} />
        )}
        {pts.map((p) => (
          <div key={p.key} className={styles.railTick} style={{ left: `${clampPct(pos(p.value))}%`, background: p.color }} />
        ))}
        {ltpValid && (
          <div className={styles.railLtp} style={{ left: `${clampPct(pos(ltp))}%` }} title={`LTP ${fmt(ltp)}`} />
        )}
      </div>
      <div className={styles.railLabels}>
        {pts.map((p) => (
          <div key={p.key} className={styles.railLabel} style={{ left: `${clampPct(pos(p.value))}%` }}>
            <span className={styles.railLabelName} style={{ color: p.color }}>{p.label}</span>
            <span className={styles.railLabelVal}>{fmt(p.value)}</span>
          </div>
        ))}
      </div>
      <div className={styles.railDir}>{isLong ? 'Long — exit at Stop (down) or Target (up)' : 'Short — exit at Stop (up) or Target (down)'}</div>
    </div>
  );
}

const Performance = () => {
  const [strategies, setStrategies] = useState([]);
  const [strategyId, setStrategyId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [trace, setTrace] = useState(null);
  const [series, setSeries] = useState([]);
  const [signals, setSignals] = useState([]);
  const [error, setError] = useState('');
  // Live market-feed status — reused from the same server-side signal the
  // Signals page uses (derived from MARKET:LAST_TICK_AT). Decided server-side
  // so live/closed never depends on the browser clock.
  const [feedLive, setFeedLive] = useState(false);
  const [feedReachable, setFeedReachable] = useState(false);

  const selected = strategies.find((s) => String(s.id) === String(strategyId));
  const symbolOptions = selected && Array.isArray(selected.symbols) ? selected.symbols : [];

  useEffect(() => {
    let active = true;
    strategyEngineService
      .getStrategies(1, 100)
      .then((res) => {
        if (!active) return;
        const list = res.data || [];
        setStrategies(list);
        if (list.length > 0) {
          const first = list.find((s) => s.is_active) || list[0];
          setStrategyId(String(first.id));
          const syms = Array.isArray(first.symbols) ? first.symbols : [];
          setSymbol(syms[0] || '');
        }
      })
      .catch(() => setError('Failed to load strategies'));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const poll = () => {
      strategyEngineService
        .getFeedStatus()
        .then((res) => {
          if (!active) return;
          setFeedReachable(true);
          setFeedLive(!!(res.data && res.data.isLive));
        })
        .catch(() => {
          if (!active) return;
          setFeedReachable(false);
          setFeedLive(false);
        });
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const handleStrategyChange = (id) => {
    setStrategyId(id);
    const s = strategies.find((x) => String(x.id) === String(id));
    const syms = s && Array.isArray(s.symbols) ? s.symbols : [];
    setSymbol(syms[0] || '');
    setTrace(null);
    setSeries([]);
    setSignals([]);
    traceFailCount.current = 0;
    setTraceError('');
  };

  // Clear the previous stock's data on symbol switch so the chart never
  // briefly shows the wrong stock before the new fetch resolves.
  const handleSymbolChange = (sym) => {
    setSymbol(sym);
    setTrace(null);
    setSeries([]);
    setSignals([]);
    traceFailCount.current = 0;
    setTraceError('');
  };

  const traceFailCount = useRef(0);
  const [traceError, setTraceError] = useState('');
  const retryTimerRef = useRef(null);

  const fetchTrace = useCallback(() => {
    if (!strategyId || !symbol) return;
    if (traceFailCount.current >= MAX_TRACE_FAILURES) return;
    strategyEngineService
      .getStrategyTrace(strategyId, symbol)
      .then((res) => {
        traceFailCount.current = 0;
        setTraceError('');
        setTrace(res.data || null);
      })
      .catch(() => {
        traceFailCount.current += 1;
        if (traceFailCount.current >= MAX_TRACE_FAILURES) {
          setTraceError('Unable to load live evaluation');
          // Bounded recovery: retry once after 30s backoff
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            traceFailCount.current = 0; // allow fetchTrace to proceed
            traceRef.current();          // single retry attempt
          }, 30000);
        }
      });
  }, [strategyId, symbol]);

  const fetchSeries = useCallback(() => {
    if (!strategyId || !symbol) return;
    strategyEngineService
      .getStrategySeries(strategyId, symbol, 500)
      .then((res) => setSeries((res.data && res.data.points) || []))
      .catch(() => {});
    strategyEngineService
      .getSignalsByStrategy(strategyId, 1, 100)
      .then((res) => setSignals((res.data || []).filter((s) => s.symbol === symbol)))
      .catch(() => {});
  }, [strategyId, symbol]);

  const traceRef = useRef(fetchTrace);
  const seriesRef = useRef(fetchSeries);
  traceRef.current = fetchTrace;
  seriesRef.current = fetchSeries;

  useEffect(() => {
    if (!strategyId || !symbol) return undefined;
    fetchTrace();
    fetchSeries();
    const t = setInterval(() => traceRef.current(), TRACE_INTERVAL);
    const s = setInterval(() => seriesRef.current(), SERIES_INTERVAL);
    return () => {
      clearInterval(t);
      clearInterval(s);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [strategyId, symbol, fetchTrace, fetchSeries]);


  const triggered = trace && trace.triggerState === 'TRIGGERED';
  const hasRisk = trace && (trace.tsl || trace.target);

  // live = feed flowing · historical = reachable but market closed · disconnected = engine unreachable
  const feedMode = !feedReachable ? 'disconnected' : feedLive ? 'live' : 'historical';
  const lastPoint = series.length > 0 ? series[series.length - 1] : null;
  const lastCandleLabel = lastPoint && lastPoint.time ? formatISTShort(lastPoint.time) : null;

  return (
    <div className={styles.wrap}>
      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Strategy</label>
          <select className={styles.select} value={strategyId} onChange={(e) => handleStrategyChange(e.target.value)}>
            {strategies.length === 0 && <option value="">No strategies</option>}
            {strategies.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.is_active ? '' : '(paused)'}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Symbol</label>
          <select className={styles.select} value={symbol} onChange={(e) => handleSymbolChange(e.target.value)}>
            {symbolOptions.length === 0 && <option value="">—</option>}
            {symbolOptions.map((sym) => (
              <option key={sym} value={sym}>{sym}</option>
            ))}
          </select>
        </div>
        <div className={styles.statePills}>
          <span
            className={`${styles.feedPill} ${
              feedMode === 'live'
                ? styles.feedLiveTag
                : feedMode === 'historical'
                ? styles.feedIdleTag
                : styles.feedDownTag
            }`}
          >
            <span className={styles.feedDot} />
            {feedMode === 'live' ? 'Live' : feedMode === 'historical' ? 'Market closed' : 'Engine offline'}
          </span>
          {trace && (
            <>
              <span className={`${styles.statePill} ${triggered ? styles.stateTriggered : styles.stateIdle}`}>
                {trace.triggerState}
              </span>
              <span className={styles.actionPill}>{trace.action}</span>
              <span className={styles.tfPill}>{trace.driverTimeframe}</span>
              <span className={styles.ltp}>LTP {fmt(trace.ltp)}</span>
            </>
          )}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!trace ? (
        <div className={styles.loading}>{traceError || 'Loading live evaluation…'}</div>
      ) : (
        <>
          {/* Top row: calculation (left) + risk/position (right) */}
          <div className={styles.topGrid}>
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>Live Calculation</h3>

              <div className={styles.subTitle}>Latest Indicator Inputs</div>
              <div className={styles.inputs}>
                {Object.entries(trace.inputs || {}).map(([tf, v]) => (
                  <div key={tf} className={styles.inputRow}>
                    <span className={styles.inputTf}>{tf}</span>
                    <span>SMA10 <b>{fmt(v.sma10)}</b></span>
                    <span>SMA20 <b>{fmt(v.sma20)}</b></span>
                    {v.volAvg5 !== undefined && (
                      <span>VA(5) <b>{fmt(v.volAvg5)}</b></span>
                    )}
                  </div>
                ))}
              </div>

              <div className={styles.subTitle}>
                Entry conditions
                <span className={`${styles.verdict} ${trace.entry.matched ? styles.vPass : styles.vFail}`}>
                  {trace.entry.matched ? 'MATCH' : 'NO MATCH'}
                </span>
              </div>
              <div className={styles.conds}>
                {trace.entry.conditions.length === 0 ? (
                  <div className={styles.muted}>No entry conditions.</div>
                ) : (
                  trace.entry.conditions.map((c, i) => <ConditionRow key={i} c={c} />)
                )}
              </div>

              {trace.exit.conditions.length > 0 && (
                <>
                  <div className={styles.subTitle}>
                    Exit conditions
                    <span className={`${styles.verdict} ${trace.exit.matched ? styles.vPass : styles.vFail}`}>
                      {trace.exit.matched ? 'MATCH' : 'NO MATCH'}
                    </span>
                  </div>
                  <div className={styles.conds}>
                    {trace.exit.conditions.map((c, i) => <ConditionRow key={i} c={c} />)}
                  </div>
                </>
              )}

              <div className={styles.stamp}>
                {feedMode === 'live' ? (
                  <>Evaluated {trace.evaluatedAt ? new Date(trace.evaluatedAt).toLocaleTimeString() : '—'} · refreshes every 3s</>
                ) : (
                  <>Last evaluated {trace.evaluatedAt ? new Date(trace.evaluatedAt).toLocaleTimeString() : '—'} · market closed — values not live</>
                )}
              </div>
            </div>

            {/* Risk / live position */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>Risk &amp; Position</h3>

              <div className={styles.riskHead}>
                <span className={`${styles.statePill} ${triggered ? styles.stateTriggered : styles.stateIdle}`}>
                  {trace.triggerState}
                </span>
                <span className={styles.ltpBig}>LTP {fmt(trace.ltp)}</span>
              </div>

              {!hasRisk ? (
                <div className={styles.muted}>No stop-loss or target configured for this strategy.</div>
              ) : (
                <>
                  <RiskRail
                    action={trace.action}
                    entry={trace.entryPrice}
                    peak={trace.extremePrice}
                    ltp={trace.ltp}
                    stop={trace.tsl ? trace.tsl.stopLevel : null}
                    target={trace.target ? trace.target.targetLevel : null}
                  />

                  <div className={styles.gauges}>
                    {trace.tsl && (
                      <div className={styles.gauge}>
                        <div className={styles.gaugeHead}>
                          <span className={styles.gaugeName}>{trace.tsl.label}</span>
                          <span className={styles.gaugeType}>{trace.tsl.type}</span>
                        </div>
                        <div className={styles.gaugeRow}><span>Entry</span><b>{fmt(trace.tsl.entryPrice)}</b></div>
                        <div className={styles.gaugeRow}><span>Peak</span><b>{fmt(trace.tsl.extremePrice)}</b></div>
                        <div className={styles.gaugeRow}><span>Stop</span><b>{fmt(trace.tsl.stopLevel)}</b></div>
                        <div className={styles.gaugeRow}><span>Price → Stop</span><b>{fmt(trace.tsl.distancePct)}%</b></div>
                      </div>
                    )}
                    {trace.target && (
                      <div className={styles.gauge}>
                        <div className={styles.gaugeHead}>
                          <span className={styles.gaugeName}>TARGET</span>
                          <span className={styles.gaugeType}>{trace.target.type}</span>
                        </div>
                        <div className={styles.gaugeRow}><span>Target</span><b>{fmt(trace.target.targetLevel)}</b></div>
                        <div className={styles.gaugeRow}><span>LTP</span><b>{fmt(trace.target.ltp)}</b></div>
                        <div className={styles.gaugeRow}><span>Price → Target</span><b>{fmt(trace.target.distancePct)}%</b></div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Full-width chart */}
          <div className={styles.panel}>
            <h3 className={styles.panelTitle}>
              SMA-10 vs SMA-20 — {feedMode === 'live' ? 'live' : 'last session'}
            </h3>
            {feedMode !== 'live' && series.length >= 2 && (
              <div className={styles.histBanner}>
                {feedMode === 'disconnected'
                  ? 'Engine unreachable — showing the last cached data.'
                  : `Market closed — showing the last session${
                      lastCandleLabel ? ` (last candle ${lastCandleLabel})` : ''
                    }. Values are not live.`}
              </div>
            )}
            <ChartLegend />
            <SmaChart points={series} signals={signals} />
          </div>
        </>
      )}
    </div>
  );
};

export default Performance;
